import { useEffect, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { toast } from "@/lib/toast";
import { getRealtimeSessionId } from "@/lib/realtimeClient";
import {
  clearPendingAuftragHandoff,
  peekPendingAuftragHandoff,
  persistPendingAuftragHandoff,
} from "@/lib/postLoginRedirect";

/**
 * Verarbeitet `?auftrag_handoff=` nach dem Scannen des Projekt-Deckblatt-QR.
 * Löst auf dem Server ein Realtime-Event aus, damit andere Sitzungen das Projekt öffnen.
 */
export function useProjectOpenHandoff() {
  const [searchParams, setSearchParams] = useSearchParams();
  const handledRef = useRef<string | null>(null);

  useEffect(() => {
    const tokenFromUrl = searchParams.get("auftrag_handoff")?.trim() || null;
    const token = tokenFromUrl || peekPendingAuftragHandoff();
    if (!token || handledRef.current === token) return;
    handledRef.current = token;
    persistPendingAuftragHandoff(token);

    void (async () => {
      try {
        const result = await api.post<{
          ok?: boolean;
          auftrag_id?: number;
          auftragsnummer?: string | null;
        }>("/auftraege/open-handoff", {
          token,
          source_session_id: getRealtimeSessionId(),
        });

        const label = result.auftragsnummer || `#${result.auftrag_id ?? ""}`;
        toast.success(`Projekt ${label} wird auf Ihren anderen Geräten geöffnet.`);
      } catch (err) {
        toast.error(
          err instanceof Error ? err.message : "Projekt konnte nicht weitergegeben werden.",
        );
      } finally {
        clearPendingAuftragHandoff();
        if (!tokenFromUrl) return;
        const next = new URLSearchParams(searchParams);
        next.delete("auftrag_handoff");
        setSearchParams(next, { replace: true });
      }
    })();
  }, [searchParams, setSearchParams]);
}
